import {
  type ActivityDto,
  colorFor,
  firstName,
  formatMoney,
  localTime,
  PAYMENT_METHOD_LABEL,
  PAYMENT_METHODS,
  type PaymentChoice,
  type PaymentMethod,
} from '@hudhuria/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { checkinVars, generalAttendance } from '~/api/actions';
import { useTodayView } from '~/api/derived';
import { useCheckinMutation } from '~/api/mutations';
import { useActivities, useCenterId, useMe, useRoster, useToday, useTodayData } from '~/api/queries';
import { ActivityBadge, ActivityPicker, NewActivityForm } from '~/components/activity';
import {
  Avatar,
  Chip,
  EmptyState,
  Overlay,
  OverlayFooter,
  OverlayHeader,
  PrimaryButton,
  SegmentedControl,
} from '~/components/ui';
import { activityMeta, quickActivities, shortName } from '~/lib/activity';
import { useBack, useFinish } from '~/lib/nav';
import { toast } from '~/lib/toast';

export const Route = createFileRoute('/_app/checkin/$studentId')({
  validateSearch: z.object({
    arrival: z.enum(['walkin', 'scan']).optional(),
    activity: z.string().optional(),
    step: z.enum(['pick', 'new']).optional(),
    /** where to land after checking in */
    from: z.string().optional(),
  }),
  component: Confirm,
});

/** Walk-in confirm: who, what they're here for, and (admins) how the fee was paid. */
function Confirm() {
  const { studentId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const back = useBack();
  const finish = useFinish();
  const me = useMe().data;
  const centerId = useCenterId();
  const today = useToday();
  const roster = useRoster();
  const activities = useActivities().data ?? [];
  const todayData = useTodayData().data;
  const view = useTodayView();
  const checkin = useCheckinMutation();
  const currency = me?.institution.currency ?? 'KES';
  const [pay, setPay] = useState<PaymentChoice>('paid');
  const [method, setMethod] = useState<PaymentMethod>(currency === 'KES' ? 'mpesa' : 'cash');

  const student = roster.data?.find((s) => s.id === studentId);
  const activity =
    activities.find((a) => a.id === search.activity) ?? generalAttendance(activities) ?? activities[0];
  const setStep = (step: 'pick' | 'new' | undefined, activityId?: string) =>
    void navigate({
      to: '.',
      search: (s) => ({ ...s, step, activity: activityId ?? s.activity }),
      replace: step === undefined,
    });

  if (!me || !roster.data) return <Overlay>{null}</Overlay>;
  if (!student) {
    return (
      <Overlay>
        <OverlayHeader back="Back" onBack={back} />
        <EmptyState>This student isn't on this device yet.</EmptyState>
      </Overlay>
    );
  }

  const isTeacher = me.staff.role === 'teacher';
  const fee = activity?.feeMinor ?? 0;
  const already = todayData?.checkins.find(
    (c) => c.studentId === student.id && c.activityId === activity?.id,
  );
  const quick = quickActivities(activities, today, activity?.id);

  const label = (() => {
    if (!activity) return 'Check in';
    const base = `Check in · ${shortName(activity.builtin ? 'General attendance' : activity.name)}`;
    if (!fee) return base;
    if (isTeacher) return `${base} · fee at desk`;
    if (pay === 'paid') return `${base} · ${formatMoney(fee, currency)} paid`;
    return pay === 'later' ? `${base} · pay later` : `${base} · waived`;
  })();

  const confirm = (a: ActivityDto) => {
    checkin.mutate(
      checkinVars({
        me,
        centerId,
        student,
        activity: a,
        arrival: search.arrival ?? 'walkin',
        payment: pay,
        method,
        localDate: today,
      }),
    );
    toast(`${firstName(student.name)} checked in`);
    finish(search.from);
  };

  return (
    <Overlay>
      <OverlayHeader back="Back" onBack={back} pb={0} />
      <div className="flex flex-1 flex-col items-center px-5 pt-5 pb-4 scroll-y">
        <Avatar
          name={student.name}
          photoUrl={student.photoUrl}
          size={100}
          radius="full"
          fontSize={36}
          display
          color={colorFor(student.name)}
        />
        <div className="mt-4 text-center font-display text-[25px] font-bold">{student.name}</div>
        <div className="mt-1 text-[14px] text-slate-500">{student.school}</div>
        <div className="mt-[11px] rounded-[20px] bg-brand-50 px-3 py-[5px] font-display text-[13px] font-bold text-brand">
          {student.ref}
        </div>

        {activity && (
          <div className="mt-6 w-full">
            <div className="mb-[10px] flex items-center justify-between">
              <div className="text-[12.5px] font-semibold text-slate-600">Here for</div>
              <button
                type="button"
                onClick={() => setStep('pick')}
                className="cursor-pointer border-none bg-transparent p-0 text-[12.5px] font-bold text-brand"
              >
                All {activities.length} activities →
              </button>
            </div>
            <button
              type="button"
              onClick={() => setStep('pick')}
              className="flex w-full cursor-pointer items-center gap-3 rounded-[15px] border-[1.5px] border-brand bg-brand-25 px-[13px] py-3 text-left"
            >
              <ActivityBadge activity={activity} today={today} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[14.5px] font-semibold">
                  {activity.builtin ? 'General attendance' : activity.name}
                </div>
                <div className="mt-0.5 text-[12px] text-slate-500">
                  {activityMeta(activity, today, currency)}
                </div>
              </div>
              <span className="text-[12.5px] font-bold text-brand">Change</span>
            </button>
            <div className="mt-[10px] flex gap-[7px] pb-0.5 scroll-x">
              {quick.map((a) => {
                const on = a.id === activity.id;
                return (
                  <button
                    type="button"
                    key={a.id}
                    onClick={() => setStep(undefined, a.id)}
                    className="flex shrink-0 cursor-pointer flex-col rounded-xl border-none px-3 py-2 text-left"
                    style={{ background: on ? '#0369a1' : '#f3f5f9', color: on ? '#fff' : '#334155' }}
                  >
                    <span className="text-[12.5px] font-semibold whitespace-nowrap">
                      {shortName(a.builtin ? 'General attendance' : a.name)}
                    </span>
                    <span
                      className="mt-px text-[10.5px]"
                      style={{ color: on ? 'rgba(255,255,255,0.8)' : '#94a3b8' }}
                    >
                      {a.time ?? 'All day'} · {a.feeMinor ? formatMoney(a.feeMinor, currency) : 'Free'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {already && (
          <div className="mt-[14px] w-full rounded-[14px] bg-emerald-50 px-[14px] py-[13px] text-[12.5px] leading-[1.45] text-emerald-800">
            <b>Already in</b> for this at {localTime(already.occurredAt, view.tz)}. Pick another activity to
            check them in again.
          </div>
        )}

        {fee > 0 && isTeacher && !already && (
          <div className="mt-[14px] w-full rounded-[14px] bg-amber-100 px-[14px] py-[13px] text-[12.5px] leading-[1.45] text-amber-800">
            <b>{formatMoney(fee, currency)} fee</b> — payment is collected at the front desk by an admin.
          </div>
        )}

        {fee > 0 && !isTeacher && !already && (
          <div className="mt-4 w-full rounded-2xl bg-fill-soft p-[14px]">
            <div className="text-[13.5px] font-semibold">Payment · {formatMoney(fee, currency)}</div>
            <SegmentedControl
              className="mt-[10px] rounded-[11px]"
              tone="seg"
              size="sm"
              value={pay}
              onChange={setPay}
              options={[
                { value: 'paid', label: 'Paid now' },
                { value: 'later', label: 'Pay later' },
                { value: 'waived', label: 'Waived' },
              ]}
            />
            {pay === 'paid' && (
              <div className="mt-[10px] flex gap-[7px]">
                {PAYMENT_METHODS.map((m) => (
                  <Chip
                    key={m}
                    on={method === m}
                    onClick={() => setMethod(m)}
                    className={method === m ? '' : 'bg-white'}
                  >
                    {PAYMENT_METHOD_LABEL[m]}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <OverlayFooter border={false}>
        <PrimaryButton disabled={!activity || !!already} onClick={() => activity && confirm(activity)}>
          {already ? 'Already checked in' : label}
        </PrimaryButton>
      </OverlayFooter>

      {search.step === 'pick' && (
        <ActivityPicker
          activities={activities}
          selectedId={activity?.id}
          onBack={back}
          onNew={() => void navigate({ to: '.', search: (s) => ({ ...s, step: 'new' }) })}
          onSelect={(a) => setStep(undefined, a.id)}
        />
      )}
      {search.step === 'new' && <NewActivityForm onBack={back} onCreated={(a) => setStep(undefined, a.id)} />}
    </Overlay>
  );
}
